/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/ResourceManager/Hardware/request/request.ts

import axios from "axios";
const API_URL = process.env.REACT_APP_API_URL || "";

/** Interfaz del payload al crear/editar hardware. */
export interface AddHardwareResourcePayload {
	datacenterId: string;
	name: string; // Datacenter displayName
	type: string; // Subtype name
	serialNumber: string;
	model: string;
	vendor: string;
	hostname: string;
	installedDate: string;
	endOfLifeDate: string;
	rackId?: string;
	rackUnit?: string;
	status: string;
	phase?: string;
	template?: string;
	targetDeviceId?: number | null; // If "module" needs to reference the target hardware
	slotAssignmentId?: number | null;
}

/** Interfaz DataCenter */
export interface DataCenterItem {
	datacenterId: string;
	name: string;
	displayName: string;
	direction: string;
	gps: string;
	capacityKW: string;
	phase: string;
	cage: string;
	rackId: string;
	rackCapacity: string;
	patchPanelODF: string;
	rackSecurityPin: string;
	status: string;
	visible: boolean;
	providerCode: string;
	city: string;
	providerId: number;
	createdAt: string;
	updatedAt: string;
	provider: {
		id: number;
		name: string;
		idAddon: string;
		createdAt: string;
		updatedAt: string;
	};
}

/** Interfaz principal Hardware */
export interface Hardware {
	id: number;
	datacenterId: string;
	name?: string;
	typeHardware: string | { name: string } | null;
	subtypeHardware: string | { name: string } | null;
	serialNumber: string;
	model: string | null;
	vendor: string | null;
	hostname: string;
	installedDate: string;
	endOfLifeDate: string | null;
	phase: string | null;
	rackId: string | null;
	rackUnit?: number | string | null;
	status: string;
	template?: string | null;
	targetDeviceId?: number | null; // If "module" needs to reference the target hardware
	slotAssignmentId?: number | null;
	monitoringIp?: string | null;
	createdAt?: string;
	updatedAt?: string;
	datacenter?: {
		displayName: string;
	};
	typeHardwareId?: number | null;
	subtypeHardwareId?: number | null;
	targetDevice?: any | null;
	slotAssignment?: any | null;
	numberOfPorts?: number; // Added for ports count
}

/**
 * Interfaz "Port".
 * Todo en string para evitar problemas con “string|number”.
 * isNew / isEditing para lógica local.
 */
export interface KEPort {
	id: number;
	subscriptionId: string;
	tenantId: number;
	name: string;
	capacity: number;
}

export interface Port {
	id: string;
	isAvailable?: boolean;
	hardwareHostname?: string;
	datacenterDisplayName?: string;
	datacenterId?: string;
	hardwareResourceId?: number;
	portName: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	KEPorts?: KEPort[];
	isNew?: boolean;
	isEditing?: boolean;
	// Additional fields from detailed port response
	clientAssignation?: string;
	decommissioned?: boolean;
	isShared?: boolean;
	createdAt?: string;
	updatedAt?: string;
	kePortCount?: number;
}
export interface HardwareType {
	id: number;
	name: string;
}
export interface HardwareSubtype {
	id: number;
	name: string;
}

/** GET datacenters */
export function getDataCentersIdsNames(token: string) {
	return axios.get<DataCenterItem[]>(`${API_URL}/v1/resource-manager/datacenters`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** GET all hardware (without filter) */
export function getAllHardware(token: string) {
	return axios.get<Hardware[]>(
		`${API_URL}/v1/resource-manager/hardware`,
		{
			headers: { Authorization: `Bearer ${token}` },
		}
	);
}

/** GET datacenter by id */
export function getHardwareByDatacenterId(token: string, datacenterId: string) {
	const encodedId = encodeURIComponent(datacenterId);
	return axios.get<Hardware[]>(
		// `${API_URL}/v1/hardwareResources/filter?datacenterId=${encodedId}`,
		`${API_URL}/v1/resource-manager/hardware/filter?datacenterId=${encodedId}`,
		{
			headers: { Authorization: `Bearer ${token}` },
		}
	);
}

/** POST /v1/resource-manager/hardware */
export function addHardwareResource(
	token: string,
	payload: AddHardwareResourcePayload
) {
	return axios.post(
		`${API_URL}/v1/resource-manager/hardware`,
		payload,
		{
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		}
	);
}

/** PUT /v1/resource-manager/hardware/:id */
export function updateHardwareResource(
	token: string,
	resourceId: string,
	payload: AddHardwareResourcePayload
) {
	const encodedId = encodeURIComponent(String(resourceId));
	return axios.put(`${API_URL}/v1/resource-manager/hardware/${encodedId}`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

/** DELETE /v1/resource-manager/hardware/:id */
export function deleteHardwareResource(token: string, resourceId: string) {
	return axios.delete(`${API_URL}/v1/resource-manager/hardware/${resourceId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

//---------PORTS--------//

export function getPorts(
	token: string,
	options?: {
		limit?: number;
		datacenterId?: string;
		hardwareResourceId?: number;
		clientAssignation?: string;
		signal?: AbortSignal;
	}
) {
	const params: Record<string, string | number> = {};
	
	if (options?.datacenterId) {
		params.datacenterId = options.datacenterId;
	}
	
	if (options?.hardwareResourceId !== undefined) {
		params.hardwareResourceId = options.hardwareResourceId;
	}
	
	if (options?.limit !== undefined) {
		params.limit = options.limit;
	}

	if (options?.clientAssignation) {
		params.clientAssignation = options.clientAssignation;
	}
	
	return axios.get(`${API_URL}/v1/resource-manager/port-allocations`, {
		params,
		headers: { Authorization: `Bearer ${token}` },
		signal: options?.signal,
	});
}

/** GET resource-manager/port-allocations/portId */
export function getPortId(token: string, portId: string) {
	return axios.get(`${API_URL}/v1/resource-manager/port-allocations/${portId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}


/** GET /v1/port/hardware/:hardwareId/ports */
export function getPortsByHardwareId(token: string, hardwareId: string) {
	//return axios.get(`${API_URL}/v1/port/hardware/${hardwareId}/ports`, {
	return axios.get(`${API_URL}/v1/resource-manager/port-allocations/by-hardware/${hardwareId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** GET hardware by Id */
export function getHardwareById(token: string, hardwareId: string) {
	return axios.get(`${API_URL}/v1/resource-manager/hardware/${hardwareId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

/** Payload para crear un puerto (POST /v1/port). */
export interface CreatePortPayload {
	hardwareResourceId: number;
	portDescription: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	clientAssignation?: string;
	isAvailable?: boolean;
	decommissioned?: boolean;
	l2Description?: string;
	l3Description?: string;
}

/** POST v1/resource-manager/port-allocations */
export function createPort(token: string, payload: CreatePortPayload) {
	return axios.post(`${API_URL}/v1/resource-manager/port-allocations`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

//Get tenants to show inside the create port form 
export function getTenantsToCreatePort(token: string, tenantId: number) {
	return axios.get(`${API_URL}/v1/tenant/list?tenantId=${tenantId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}
/** Payload para actualizar un puerto (PUT /v1/port/:id). */
export interface UpdatePortPayload {
	portDescription: string;
	portId: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	clientAssignation?: string;
	isAvailable?: boolean;
	decommissioned?: boolean;
	isShared?: boolean;
	l2Description?: string;
	l3Description?: string;
}

/** PUT /v1/port/:id */
export function updatePort(
	token: string,
	portId: string,
	payload: UpdatePortPayload
) {
	return axios.put(`${API_URL}/v1/resource-manager/port-allocations/${portId}`, payload, {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	});
}

/** DELETE /v1/port/:id */
export function deletePort(token: string, portId: string) {
	return axios.delete(`${API_URL}/v1/resource-manager/port-allocations/${portId}`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

export function getHardwareTypes(token: string) {
	return axios.get(`${API_URL}/v1/resource-manager/hardware/types`, {
		headers: { Authorization: `Bearer ${token}` },
	});
}

export function getSubTypesByTypeHardware(
	token: string,
	typeHardwareId: number
) {
	return axios.get<HardwareSubtype[]>(
		`${API_URL}/v1/resource-manager/hardware/types/${typeHardwareId}/subtypes`,
		{ headers: { Authorization: `Bearer ${token}` } }
	);
}
